import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // One-shot CORS smoke check on mount. If the browser blocks the call due to a
  // misconfigured Access-Control-Allow-Origin (e.g. wildcard + credentials),
  // surface a loud toast so we catch the regression before customers do.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/health/cors")
      .catch((err) => {
        if (cancelled) return;
        const blockedByCors =
          err?.message === "Network Error" || err?.code === "ERR_NETWORK";
        if (blockedByCors) {
          console.error("[CORS check] Failed:", err);
          toast.error(
            "Heads up: the site can't talk to its server (CORS). Sign-in & booking will fail until this is fixed.",
            { duration: 12000 }
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signup = async (email, password, name) => {
    const { data } = await api.post("/auth/signup", { email, password, name });
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  const updateProfile = async (patch) => {
    const { data } = await api.post("/auth/profile", patch);
    setUser(data.user);
    return data.user;
  };

  const value = {
    user,
    loading,
    refresh,
    signup,
    login,
    loginWithGoogleSession,
    logout,
    updateProfile,
    isAuthed: !!user,
    hasCard: !!user?.has_card,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
